import type { FieldInstanceValueFields, TableFieldInstanceFields } from "@regrello/graphql-api";

import { getErrorMessageWithPayload } from "../../../../../utils/getErrorMessageWithPayload";

/**
 * Extracts tabular array values from a field instance, handling multiple values per row.
 * Intended for use with multi-select fields in particular.
 *
 * @param params - Configuration object containing:
 * @param params.fieldInstance - The field instance to extract values from
 * @param params.fieldInstanceValueTypeName - Expected typename(s) for field instance values
 * @param params.errorMessageIfMultipleValues - Error message if multiple value arrays found
 * @param params.errorMessageIfWrongValueType - Error message if value has wrong type
 * @param params.getterIfNoValue - Function to get default value when no values exist
 * @param params.getterIfTabularValue - Function to extract tabular values from field instance value
 * @returns Array of row objects containing row numbers and arrays of values
 * @throws Error if multiple value arrays found or if value has wrong type
 */
export function extractMultipleTabularArraysOrThrow<
  TReturnValue,
  TTabularValue extends Array<{ value: TReturnValue[]; rowNumber: number }>,
  TFieldInstanceValueTypeName extends FieldInstanceValueFields["__typename"],
  TFieldInstanceValue extends Extract<FieldInstanceValueFields, { __typename: TFieldInstanceValueTypeName }>,
>(params: {
  fieldInstance: TableFieldInstanceFields;
  fieldInstanceValueTypeName: TFieldInstanceValueTypeName | TFieldInstanceValueTypeName[];
  errorMessageIfMultipleValues: string;
  errorMessageIfWrongValueType: string;
  getterIfNoValue: () => TTabularValue;
  getterIfTabularValue: (fieldInstanceValue: TFieldInstanceValue) => TTabularValue;
}): TTabularValue {
  const {
    fieldInstance,
    fieldInstanceValueTypeName,
    errorMessageIfMultipleValues,
    errorMessageIfWrongValueType,
    getterIfNoValue,
    getterIfTabularValue,
  } = params;

  const { values } = fieldInstance;

  if (values.length > 1) {
    throw new Error(getErrorMessageWithPayload(errorMessageIfMultipleValues, { fieldInstance }));
  }

  if (values.length === 0) {
    return getterIfNoValue();
  }

  const representativeSingleValue = values[0];

  const permittedTypeNames =
    typeof fieldInstanceValueTypeName === "string" ? [fieldInstanceValueTypeName] : fieldInstanceValueTypeName;

  if (!permittedTypeNames.includes(representativeSingleValue.__typename as TFieldInstanceValueTypeName)) {
    throw new Error(getErrorMessageWithPayload(errorMessageIfWrongValueType, { fieldInstance }));
  }

  // (clewis): TS isn't smart enough to know that the type has been narrowed properly based on the
  // __typename, but it works as expected in practice.
  //
  return getterIfTabularValue(representativeSingleValue as TFieldInstanceValue);
}
